import Vue from 'vue'
import {
  CURRENCY,
  catalogItem,
  orderItems,
  purchaseEcommerce,
  pushEcommerceEvent,
} from '~/utils/gtmEcommerce'
import {
  newsletterConfirmedEvent,
  newsletterSignupEvent,
} from '~/utils/newsletterOffer'
import {
  operationBadgeProductClickEvent,
  operationBannerClickEvent,
  operationCodeApplyEvent,
} from '~/utils/commercialOperation'

function generateEventId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function getUserData(cart) {
  const shipping = cart.shipping_address || {}
  return {
    email: cart.user?.email,
    phone_number: shipping.phone,
    address: {
      first_name: shipping.firstname,
      last_name: shipping.lastname,
      street: shipping.address,
      city: shipping.city,
      postal_code: shipping.postcode,
      country: shipping.country?.iso,
    },
  }
}

export default ({ app }) => {
  if (!process.client) return

  window.dataLayer = window.dataLayer || []

  const language = () => app.i18n?.locale
  if (language()) {
    window.dataLayer.push({ language: language() })
  }

  app.router.afterEach(() => {
    if (app.store.state.cart) {
      window.dataLayer.push({
        event: 'user_data_update',
        user_data: getUserData(app.store.state.cart),
      })
    }

    window.dataLayer.push({
      event: 'page_view',
      event_id: generateEventId(),
      ...(language() ? { language: language() } : {}),
    })
  })

  Vue.prototype.$trackViewItem = function (product, variant) {
    if (!product || !variant) return null
    const price = Number(variant.user_price_ttc || variant.price_ttc || 0)
    if (!price || price <= 0) return null

    const eventId = generateEventId()
    pushEcommerceEvent(window.dataLayer, {
      event: 'view_item',
      event_id: eventId,
      ecommerce: {
        currency: CURRENCY,
        value: price,
        items: [catalogItem(product, variant, 1, price)],
      },
      content_name: variant.name || product.name || '',
      content_ids: [String(variant.gtin || variant.id)],
      content_type: 'product',
    })
    return eventId
  }

  Vue.prototype.$trackBeginCheckout = function (order) {
    if (!order) return null
    const amount = Number(order.total_ttc || 0)
    if (!amount || amount <= 0) return null

    const eventId = generateEventId()
    pushEcommerceEvent(window.dataLayer, {
      event: 'begin_checkout',
      event_id: eventId,
      ecommerce: {
        value: amount,
        currency: CURRENCY,
        items: orderItems(order),
      },
    })
    return eventId
  }

  Vue.prototype.$trackAddShippingInfo = function (order) {
    if (!order) return
    const amount = Number(order.total_ttc || 0)
    if (!amount || amount <= 0) return

    pushEcommerceEvent(window.dataLayer, {
      event: 'add_shipping_info',
      event_id: generateEventId(),
      ecommerce: {
        value: amount,
        currency: CURRENCY,
        items: orderItems(order),
      },
    })
  }

  Vue.prototype.$trackAddPaymentInfo = function (order, paymentType = null) {
    if (!order) return
    const amount = Number(order.total_ttc || 0)
    if (!amount || amount <= 0) return

    pushEcommerceEvent(window.dataLayer, {
      event: 'add_payment_info',
      event_id: generateEventId(),
      ecommerce: {
        value: amount,
        currency: CURRENCY,
        ...(paymentType ? { payment_type: paymentType } : {}),
        items: orderItems(order),
      },
    })
  }

  Vue.prototype.$trackAddToCart = function (product, variant, quantity = 1) {
    if (!product || !variant) return null
    const price = Number(variant.user_price_ttc || variant.price_ttc || 0)
    if (!price || price <= 0) return null

    const totalValue = price * quantity
    const eventId = generateEventId()

    pushEcommerceEvent(window.dataLayer, {
      event: 'add_to_cart',
      event_id: eventId,
      ecommerce: {
        currency: CURRENCY,
        value: totalValue,
        items: [catalogItem(product, variant, quantity, price)],
      },
      content_ids: [String(variant.gtin || variant.id)],
      content_type: 'product',
    })
    return eventId
  }

  Vue.prototype.$trackPurchase = function (order) {
    if (!order || !order.id) return null

    const totalAmount = Number(order.total_ttc || 0)
    if (!totalAmount || totalAmount <= 0) return null

    if (!order.order_variants || !order.order_variants.length) return null

    const gclid = order.gclid || null
    const shipping = order.shipping_address || {}
    const user = order.user || {}

    const validVariants = (order.order_variants || []).filter(
      item => item.variant && (item.variant.gtin || item.variant.id)
    )

    const contentIds = validVariants.map(item =>
      String(item.variant.gtin || item.variant.id)
    )

    const numItems = validVariants.reduce(
      (total, item) => total + (item.quantity || 1),
      0
    )

    if (contentIds.length === 0 || numItems === 0) return null

    const eventId = generateEventId()

    pushEcommerceEvent(window.dataLayer, {
      event: 'purchase',
      event_id: eventId,
      ecommerce: purchaseEcommerce(order),
      content_ids: contentIds,
      content_type: 'product',
      num_items: numItems,
      user_data: {
        email: user.email,
        phone_number: shipping.phone,
        address: {
          first_name: shipping.firstname,
          last_name: shipping.lastname,
          street: shipping.address,
          city: shipping.city,
          postal_code: shipping.postcode,
          country: shipping.country?.iso,
        },
      },
      ...(gclid ? { gclid } : {}),
    })
    return eventId
  }

  Vue.prototype.$trackNewsletterSignup = function (status, source) {
    const eventId = generateEventId()
    window.dataLayer.push(
      newsletterSignupEvent({ status, source, language: language(), eventId })
    )
    return eventId
  }

  Vue.prototype.$trackOperationBannerClick = function (phase) {
    const eventId = generateEventId()
    window.dataLayer.push(
      operationBannerClickEvent({ phase, language: language(), eventId })
    )
    return eventId
  }

  Vue.prototype.$trackOperationBadgeProductClick = function (product, code) {
    const eventId = generateEventId()
    window.dataLayer.push(
      operationBadgeProductClickEvent({
        productId: product?.id,
        code,
        language: language(),
        eventId,
      })
    )
    return eventId
  }

  Vue.prototype.$trackOperationCodeApply = function (code, source) {
    const eventId = generateEventId()
    window.dataLayer.push(
      operationCodeApplyEvent({ code, source, language: language(), eventId })
    )
    return eventId
  }

  Vue.prototype.$trackNewsletterConfirmed = function () {
    const eventId = generateEventId()
    window.dataLayer.push(
      newsletterConfirmedEvent({ language: language(), eventId })
    )
    return eventId
  }
}
